import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Repeat, Loader2, Play, Eye, Clock, CheckCircle2, XCircle, Store as StoreIcon } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtMoney, fmtDate, fmtDateTime } from "@/components/status";

export default function StandingOrders() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [result, setResult] = useState(null); // preview or run summary
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");

  const loadAll = () => {
    adminApi.standingSettings().then(setSettings).catch(() => {});
    adminApi.standingTemplates().then(setTemplates).catch(() => {});
    adminApi.standingHistory(10).then(setHistory).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { setSettings(await adminApi.saveStandingSettings({ enabled: next.enabled, hour: next.hour, minute: next.minute })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); loadAll(); }
  };

  const runPreview = async () => {
    setBusy("preview");
    try { setResult({ ...(await adminApi.standingPreview()), _kind: "preview" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };
  const runNow = async () => {
    setBusy("run");
    try {
      const r = await adminApi.standingRunNow();
      toast.success(`${r.created.length} orders generated`);
      setResult({ ...r, _kind: "run" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  const activeTemplates = templates.filter((t) => (t.status || "").toLowerCase() !== "cancelled");

  return (
    <div data-testid="admin-standing-orders">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold text-[#28282E]">Standing Orders</h1>
        <p className="text-sm text-[#28282E]/70">Preview and run the weekly auto-generated orders built from each store's standing template.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          {/* Actions */}
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={runPreview} disabled={!!busy} data-testid="standing-preview-btn" variant="outline"
                className="rounded-full border-[#EBBAD1] text-[#28282E] hover:bg-[#FBEEF4]">
                {busy === "preview" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Preview next run
              </Button>
              <Button onClick={runNow} disabled={!!busy || !settings?.enabled} data-testid="standing-run-btn"
                className="rounded-full bg-[#28282E] text-white hover:bg-[#1F1F24]">
                {busy === "run" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Run now
              </Button>
              {!settings?.enabled && <span className="text-xs text-amber-600">Enable the generator to run now.</span>}
            </div>

            {result && (
              <div className="mt-5" data-testid="standing-result">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#28282E]">
                  {result._kind === "preview" ? "Preview" : "Run"} result — {result.created.length} to create, {result.skipped.length} skipped
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Created ({result.created.length})</div>
                    <ul className="max-h-64 space-y-1.5 overflow-auto">
                      {result.created.map((c) => (
                        <li key={c.order_id} className="rounded-lg bg-emerald-50 px-3 py-2 text-xs">
                          <span className="font-semibold text-[#28282E]">{c.store_code}</span> · {c.item_count} items · {fmtMoney(c.total)} · {fmtDate(c.delivery_date)}
                        </li>
                      ))}
                      {result.created.length === 0 && <li className="text-xs text-[#28282E]/55">Nothing to create.</li>}
                    </ul>
                  </div>
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase text-[#28282E]/70"><XCircle className="h-3.5 w-3.5" /> Skipped ({result.skipped.length})</div>
                    <ul className="max-h-64 space-y-1.5 overflow-auto">
                      {result.skipped.map((s, i) => (
                        <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                          <span className="font-semibold text-[#28282E]">{s.store_code || "—"}</span> · <span className="text-[#28282E]/70">{s.reason}</span>
                        </li>
                      ))}
                      {result.skipped.length === 0 && <li className="text-xs text-[#28282E]/55">None skipped.</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Templates */}
          <div className="rounded-3xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-3 text-sm font-bold text-[#28282E]">Standing templates ({activeTemplates.length})</div>
            {activeTemplates.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-[#28282E]/55">No standing orders yet. Stores create these from a placed order.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="standing-templates">
                {activeTemplates.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 px-5 py-3">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#FBEEF4] text-[#28282E]"><StoreIcon className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-[#28282E]">{t.store_code}</div>
                      <div className="text-xs text-[#28282E]/70">{t.item_count} items · {fmtMoney(t.total || 0)}</div>
                    </div>
                    {t.store_on_hold && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">On hold</span>}
                    <div className="flex flex-wrap gap-1">
                      {(t.store_delivery_days || []).map((d) => (
                        <span key={d} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-[#28282E]/70">{d}</span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Settings + history */}
        <div className="space-y-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#28282E]"><Repeat className="h-4 w-4 text-[#28282E]" /> Auto-run schedule</div>
            {!settings ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#28282E]/55" />
            ) : (
              <div className="space-y-4">
                <label className="flex items-center justify-between rounded-xl bg-[#FBEEF4] px-3 py-2.5">
                  <span className="text-sm font-semibold text-[#28282E]">Enabled</span>
                  <Switch checked={!!settings.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="standing-enabled-switch" />
                </label>
                <div className="flex items-end gap-3">
                  <div>
                    <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Hour (0-23)</Label>
                    <Input type="number" min={0} max={23} value={settings.hour ?? 0} onChange={(e) => setSettings({ ...settings, hour: Number(e.target.value) })}
                      onBlur={(e) => saveSettings({ hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })} data-testid="standing-hour" className="w-24" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Minute</Label>
                    <Input type="number" min={0} max={59} value={settings.minute ?? 0} onChange={(e) => setSettings({ ...settings, minute: Number(e.target.value) })}
                      onBlur={(e) => saveSettings({ minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })} data-testid="standing-minute" className="w-24" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#28282E]/70"><Clock className="h-3.5 w-3.5" /> Next run: <span className="font-semibold text-[#28282E]">{fmtDateTime(settings.next_run_at)}</span></div>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="mb-3 text-sm font-bold text-[#28282E]">Run history</div>
            {history.length === 0 ? (
              <p className="text-xs text-[#28282E]/55">No runs recorded yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="standing-history">
                {history.map((h) => (
                  <li key={h.run_id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold uppercase text-[#28282E]">{h.mode}</span>
                      <span className="text-[#28282E]/55">{fmtDateTime(h.finished_at || h.started_at)}</span>
                    </div>
                    <div className="mt-0.5 text-[#28282E]/70">{(h.created || []).length} created · {(h.skipped || []).length} skipped</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
